import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bold,
  Italic,
  Underline,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link2,
  Pilcrow,
  Eraser,
  Code,
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

interface ToolbarButtonProps {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ title, onClick, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
  >
    {children}
  </button>
);

const Divider: React.FC = () => (
  <span className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1 self-center" aria-hidden="true" />
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = 280,
}) => {
  const { t } = useTranslation();
  const ph = placeholder ?? t('blogSeo.editor.placeholderDefault', 'Escreva aqui...');
  const editorRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [showHtml, setShowHtml] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!value);

  // Controlled sync without cursor jumps: only write into the div when the
  // incoming value differs from its current content AND the editor is not focused.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (showHtml) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
    }
    setIsEmpty(!value);
  }, [value, showHtml]);

  const exec = useCallback(
    (command: string, arg?: string) => {
      const el = editorRef.current;
      if (el) el.focus();
      document.execCommand(command, false, arg);
      if (el) {
        onChange(el.innerHTML);
        setIsEmpty(!el.innerHTML || el.innerHTML === '<br>');
      }
    },
    [onChange],
  );

  const handleLink = useCallback(() => {
    const url = window.prompt(t('blogSeo.editor.linkPrompt', 'URL do link:'), 'https://');
    if (url) exec('createLink', url);
  }, [exec, t]);

  const handleInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    onChange(html);
    setIsEmpty(!html || html === '<br>');
  }, [onChange]);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-t-lg border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2 py-1.5">
        <ToolbarButton title={t('blogSeo.editor.bold', 'Negrito')} onClick={() => exec('bold')}>
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title={t('blogSeo.editor.italic', 'Itálico')} onClick={() => exec('italic')}>
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title={t('blogSeo.editor.underline', 'Sublinhado')} onClick={() => exec('underline')}>
          <Underline className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton title={t('blogSeo.editor.h2', 'Título H2')} onClick={() => exec('formatBlock', '<h2>')}>
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title={t('blogSeo.editor.h3', 'Título H3')} onClick={() => exec('formatBlock', '<h3>')}>
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title={t('blogSeo.editor.paragraph', 'Parágrafo')} onClick={() => exec('formatBlock', '<p>')}>
          <Pilcrow className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton title={t('blogSeo.editor.bulletList', 'Lista com marcadores')} onClick={() => exec('insertUnorderedList')}>
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title={t('blogSeo.editor.numberedList', 'Lista numerada')} onClick={() => exec('insertOrderedList')}>
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title={t('blogSeo.editor.quote', 'Citação')} onClick={() => exec('formatBlock', '<blockquote>')}>
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton title={t('blogSeo.editor.link', 'Inserir link')} onClick={handleLink}>
          <Link2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title={t('blogSeo.editor.clearFormat', 'Limpar formatação')} onClick={() => exec('removeFormat')}>
          <Eraser className="w-4 h-4" />
        </ToolbarButton>
        <div className="ml-auto">
          <button
            type="button"
            title={t('blogSeo.editor.editHtml', 'Editar HTML')}
            aria-label={t('blogSeo.editor.editHtml', 'Editar HTML')}
            aria-pressed={showHtml}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowHtml((v) => !v)}
            className={`inline-flex items-center gap-1 h-8 px-2 rounded-md text-xs font-medium transition-colors ${
              showHtml
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Code className="w-4 h-4" />
            HTML
          </button>
        </div>
      </div>

      {/* Editor body */}
      {showHtml ? (
        <textarea
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsEmpty(!e.target.value);
          }}
          spellCheck={false}
          className="block w-full resize-y rounded-b-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-3 font-mono text-xs leading-relaxed focus:outline-none"
          style={{ minHeight }}
          placeholder={ph}
        />
      ) : (
        <div className="relative">
          {isEmpty && !focused && (
            <span className="pointer-events-none absolute top-3 left-3 text-sm text-gray-400 dark:text-gray-500 select-none">
              {ph}
            </span>
          )}
          <div
            ref={editorRef}
            role="textbox"
            aria-multiline="true"
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              handleInput();
            }}
            style={{ minHeight }}
            className="text-sm leading-relaxed [&_h2]:text-xl [&_h2]:font-bold [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:my-2 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_a]:text-primary-600 [&_a]:underline dark:bg-gray-900 bg-white text-gray-800 dark:text-gray-200 border-0 rounded-b-lg p-3 overflow-auto focus:outline-none"
          />
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
